/**
 * @module playerManager
 */

import { playerListeners, emit, isEventSupported } from "../../../web/manager/player/listeners";
import states from "../../../web/manager/player/states";
import heartbeat from "../../../web/manager/player/mediaListsHeartbeat";
import { playerErrorCodes } from "./webPlayerErrors";
import {
  tracksInitialize,
  getAvailableSubtitleTracks,
  getCurrentSubtitleTrack,
  setSubtitleTrack,
  getAvailableAudioTracks,
  getCurrentAudioTrack,
  setAudioTrack
} from "../../../bs.webos/manager/player/tracks";

import { youbora } from "./youbora";
import Watcher from "../../../utils/Watcher";

//for now LG uses same youbora plugin as bitmovin
const youboraPlugin = "bitmovin";
let playerStateWatcher = null;
let heartbeatFromConfigPeriod = null;
let settingBitmovinCurrentPlayTime = 0;

function canStartPlayback(refCanStartPlayback) {
  return refCanStartPlayback !== null;
}

/**
 * Player (HISENSE TV) mixin methods for the SDK.
 *
 * @param sdk
 * @returns {{setup(), pause(), play(), stop(), seek(),setCurrentTime(),setCurrentBitrate(), getCurrentTime(), destroy(), error()}}
 */
export default {
  // FIXME Why are we exposing this? Also, it's confusing with the 'states' variable that get's also exposed
  state: states.NONE,

  prevState: states.NONE,

  didFirstCurrentTime: false,

  /**
   * Initializes Samsung Tizen player with a given configuration.
   * @param {Object} config - Configuration information from the video to play.
   * @param {string} config.manifest - Manifest URL for media content to play.
   * @param {string} config.videoType - Content type: "series" | "movie"| "trailer"
   * @param {(string|string[])} config.releasePids - Release PIDs for DRM protected content.
   */
  async setup(config) {
    try {
      tracksInitialize(config);
    } catch (e) {
      console.log("error getting subtitles content");
    }
    try {
      if (canStartPlayback(config.refCanStartPlayback)) {
        emit("init");
        emit("state", this.state);
        playerStateWatcher = Watcher(this, ["state"]);
        playerStateWatcher.observe("state", () => {
          if (this.prevState !== this.state) {
            this.prevState = this.state;
            emit("state", this.state);
          }
        });
        //intialize tracks
        tracksInitialize(config);
      }
    } catch (e) {
      console.log("error getting content");
    }
  },

  configSuccess(config) {
    config.videoType !== "trailer" &&
      heartbeat.initialize({ ...config, heartbeatPeriod: heartbeatFromConfigPeriod }, this);
  },

  setHeartbeat(heartBeat) {
    heartbeatFromConfigPeriod = heartBeat;
  },

  resetHeartBeat(config) {
    config.videoType !== "trailer" && heartbeat.reset();
  },

  updateState(state) {},

  pause(sendPlaybackAnalyticsEvent = true) {
    emit("pause");
    sendPlaybackAnalyticsEvent && youbora.sendEvent(youbora.events.pause);
  },

  play() {
    emit("play");
    youbora.sendEvent(youbora.events.resume);
  },

  setCurrentBitrate() {},

  getCurrentBitrate() {
    return "0";
  },

  async getCurrentBandwidth() {
    return "";
  },

  stop(isYouboraEventSent = true) {
    emit("stop");
    isYouboraEventSent && youbora.sendEvent(youbora.events.stop);
  },

  willStop() {
    emit("willStop");
  },

  ready() {
    emit("state", "READY");
  },

  seek(position) {
    emit("seek");
    const reportSeekEnd = () => {
      youbora.sendEvent(youbora.events.seekEnd);
      this.off("bufferEnd", reportSeekEnd);
    };
    this.on("bufferEnd", reportSeekEnd);
    youbora.sendEvent(youbora.events.seekBegin);
  },

  setCurrentTime(currentPLayTimeInMilliSeconds) {
    this.settingBitmovinCurrentPlayTime = currentPLayTimeInMilliSeconds;
  },

  getCurrentTime() {
    return this.settingBitmovinCurrentPlayTime;
  },

  getDuration() {},

  on(eventName, callback) {
    if (isEventSupported(eventName)) {
      playerListeners[eventName].push(callback);
    }
  },
  off(eventName, listener) {
    if (isEventSupported(eventName)) {
      const listenerIndex = playerListeners[eventName].findIndex(func => listener === func);
      listenerIndex !== -1 && playerListeners[eventName].splice(listenerIndex, 1);
    }
  },
  destroy() {
    emit("destroy");
    playerStateWatcher && playerStateWatcher.destroy();
    playerStateWatcher = null;
    // TODO
  },
  error() {
    // TODO
  },
  getAvailableSubtitleTracks,
  getCurrentSubtitleTrack,
  setSubtitleTrack,
  getAvailableAudioTracks,
  getCurrentAudioTrack,
  setAudioTrack,
  playerErrorCodes,
  states,
  // TODO Create an "analytics" namespace here that contains the 4 items below
  initPlaybackAnalytics(player = null, playerConfig = null) {
    const isloaded = youbora.loadYouboraLib(this.playbackAnalytics.libSrc, () => {
      youbora.initialize.call(this, youboraPlugin, player);
      youbora.setOptions.call(this, playerConfig);
      youbora.sendEvent(youbora.events.start);
    });
    if (isloaded === false) {
      youbora.resetAdapter.call(this, youboraPlugin, player);
    }
  },

  startPlaybackAnalytics(config) {
    return new Promise(async resolve => {
      await youbora.setOptions.call(this, config);
      youbora.sendEvent(youbora.events.start);
      resolve();
    });
  },
  youboraFireError(config, event, data) {
    return new Promise(async resolve => {
      await youbora.setOptions.call(this, config);
      youbora.sendEvent(event, data.code, data.name);
      resolve();
    });
  },
  sendPlaybackAnalyticsEvent(event, data) {
    youbora.sendEvent(event, data.code, data.name);
  },
  analyticsEvents: youbora.events
};
